import { useCallback, useEffect, useState } from "react";
import {
  hasOpenedOdooLiveChat,
  markOdooLiveChatOpened,
  subscribeOdooLiveChat
} from "services/odoo-live-chat/odoo-live-chat";

import { IUseOdooLiveChatBridge } from "./use-odoo-live-chat-bridge.types";

// Mirrors the Odoo bridge (window.clLivechat) into React state: whether the
// launcher should be visible, availability once the widget is loaded, and the
// unread count. Also remembers that the launcher was shown or the chat opened,
// so it stays on every page of this tab.
export const useOdooLiveChatBridge = (
  isBimbelPage: boolean
): IUseOdooLiveChatBridge => {
  const [isVisible, setIsVisible] = useState(true);
  // Once the launcher has been shown (Bimbel) or the chat opened in this tab,
  // the launcher stays on every page.
  const [hasOpenedChat, setHasOpenedChat] = useState(false);
  // Availability from the bridge once the widget is loaded. Null means unknown.
  const [bridgeAvailable, setBridgeAvailable] = useState<boolean | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    // Showing the launcher on Bimbel is enough to keep it on later pages.
    if (isBimbelPage) markOdooLiveChatOpened();
    setHasOpenedChat(isBimbelPage || hasOpenedOdooLiveChat());
  }, [isBimbelPage]);

  useEffect(
    () =>
      subscribeOdooLiveChat((api) => {
        // Older bridge versions cannot tell an open window from a minimized
        // one and show their own bubble, so hide for any active chat.
        setIsVisible(
          api.isChatOpen ? !api.isChatOpen() : !api.hasActiveChat?.()
        );
        if (api.hasActiveChat?.() || api.isChatOpen?.()) {
          markOdooLiveChatOpened();
          setHasOpenedChat(true);
        }
        setBridgeAvailable(api.isAvailable());
        setUnreadCount(api.unreadCount?.() ?? 0);
      }),
    []
  );

  const markOpened = useCallback(() => {
    markOdooLiveChatOpened();
    setHasOpenedChat(true);
  }, []);

  return { isVisible, hasOpenedChat, bridgeAvailable, unreadCount, markOpened };
};
